import { Box, Typography } from "@mui/joy";

/**
 * Shared Apple-style form & dialog building blocks.
 *
 * Usage pattern (see FormGig for the reference implementation):
 * - Dialog PaperProps: `PaperProps={{ sx: dialogPaperSx }}`
 * - Title block: <DialogHeader title="…" subtitle="…" />
 * - Field groups: <GroupCaption>The basics</GroupCaption> +
 *   <Sheet variant="soft" sx={groupSheetSx}>…fields…</Sheet>
 * - Fields: startDecorator={<FieldLabel icon="fa-solid fa-…" color="#…" label="…" />}
 * - Action buttons: sx={pillButtonSx}
 */

export const dialogPaperSx = { borderRadius: "20px" } as const;

export const groupSheetSx = {
  borderRadius: "14px",
  overflow: "hidden",
  flexShrink: 0,
} as const;

export const pillButtonSx = {
  borderRadius: "999px",
  fontWeight: 600,
} as const;

// iOS system palette for field icon tiles
export const tileColors = {
  blue: "#007AFF",
  indigo: "#5856D6",
  purple: "#AF52DE",
  orange: "#FF9500",
  teal: "#5AC8FA",
  green: "#34C759",
  red: "#FF3B30",
  pink: "#FF2D55",
  gray: "#8E8E93",
} as const;

/**
 * iOS Settings-style colored icon tile + label, used as field decorator.
 *
 * @param root0
 * @param root0.icon FontAwesome class, e.g. "fa-solid fa-signature"
 * @param root0.color tile background (see tileColors)
 * @param root0.label optional text label next to the tile
 * @returns {ReactElement} FieldLabel
 */
export function FieldLabel({
  icon,
  color,
  label,
}: {
  icon: string;
  color: string;
  label?: string;
}) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
      <Box
        sx={{
          width: 24,
          height: 24,
          borderRadius: "7px",
          background: color,
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 11,
          boxShadow: "inset 0 -4px 6px rgba(0, 0, 0, 0.08)",
        }}
      >
        <i className={icon}></i>
      </Box>
      {label ? (
        <Typography
          level="body2"
          sx={{
            fontWeight: 500,
            color: "var(--mui-palette-text-primary)",
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </Typography>
      ) : (
        []
      )}
    </Box>
  );
}

/**
 * Uppercase group caption with optional "Optional" pill.
 *
 * @param root0
 * @param root0.children
 * @param root0.optional
 * @returns {ReactElement} GroupCaption
 */
export function GroupCaption({
  children,
  optional = false,
}: {
  children: string;
  optional?: boolean;
}) {
  return (
    <Box
      sx={{ display: "flex", alignItems: "center", gap: 1, px: 0.5, mt: 0.5 }}
    >
      <Typography
        level="body3"
        sx={{
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          fontSize: "0.6875rem",
          color: "var(--mui-palette-text-tertiary)",
        }}
      >
        {children}
      </Typography>
      {optional ? (
        <Typography
          sx={{
            fontSize: "0.6875rem",
            fontWeight: 600,
            px: 0.75,
            borderRadius: "999px",
            background: "rgba(120, 120, 128, 0.12)",
            color: "var(--mui-palette-text-secondary)",
          }}
        >
          Optional
        </Typography>
      ) : (
        []
      )}
    </Box>
  );
}

/**
 * Bold dialog title with an optional friendly subtitle.
 *
 * @param root0
 * @param root0.title
 * @param root0.subtitle
 * @returns {ReactElement} DialogHeader
 */
export function DialogHeader({
  title,
  subtitle,
}: {
  title: string;
  subtitle?: string;
}) {
  return (
    <Box>
      <Typography level="h5" sx={{ fontWeight: 700, letterSpacing: "-0.01em" }}>
        {title}
      </Typography>
      {subtitle ? (
        <Typography
          level="body2"
          sx={{ color: "var(--mui-palette-text-secondary)" }}
        >
          {subtitle}
        </Typography>
      ) : (
        []
      )}
    </Box>
  );
}
